import { getApiErrorMessage } from '../../utils/apiError';
import { authService } from '../../services/auth';
import { getBlockedActionMessage, normalizeRelatedDocuments } from '../warehouse/blockedActions';

/**
 * Single home for turning a customer-order API failure into a Polish sentence.
 * Sibling of `modules/warehouse/pzErrorMessages.ts`; the same keys render the
 * `blockedActions` alert on the order card (see `blockedActions.ts`).
 */
export const ORDER_ERROR_CODE_KEYS: Record<string, string> = {
  ORDER_STATUS_TERMINAL: 'orders:error.statusTerminal',
  ORDER_INVALID_STATUS_TRANSITION: 'orders:error.invalidStatusTransition',
  ORDER_DELIVERY_WZ_MISSING: 'orders:error.deliveryWzMissing',
  ORDER_DELIVERY_WZ_UNSIGNED: 'orders:error.deliveryWzUnsigned',
  ORDER_COMMERCIALLY_FROZEN: 'orders:error.commerciallyFrozen',
  ORDER_LINKED_TO_TRANSPORTS: 'orders:error.linkedToTransports',
  ORDER_LINKED_TO_WZ_DOCUMENTS: 'orders:error.linkedToWzDocuments',
  ORDER_PRODUCT_HAS_TRANSPORT_CARGO: 'orders:error.productHasTransportCargo',
  ORDER_EARMARK_TERMINAL_STATUS: 'orders:error.earmarkTerminalStatus',
};

const ORDER_BLOCKING_ERROR_CODES = new Set([
  ...Object.keys(ORDER_ERROR_CODE_KEYS),
  'INVOICE_APPROVAL_REQUIRED',
]);

export function isBlockingOrderError(error: any): boolean {
  const code = error?.response?.data?.code;
  return typeof code === 'string' && ORDER_BLOCKING_ERROR_CODES.has(code);
}

/**
 * `INVOICE_APPROVAL_REQUIRED` points the operator to the sales-invoice form —
 * but only users on the invoice allowlist can open it. Everyone else is told
 * to hand the document over instead of being sent to a screen they cannot see.
 */
function invoiceApprovalRequiredKey(): string {
  return authService.canManageInvoiceImports()
    ? 'orders:error.invoiceApprovalRequired'
    : 'orders:error.invoiceApprovalRequiredNoAccess';
}

/**
 * Older delete rejections carry `transportRouteNumbers` / `wzNumbers` rather
 * than `relatedDocuments`; keep naming the documents either way.
 */
function relatedDocumentsFromPayload(data: any) {
  if (Array.isArray(data?.relatedDocuments) && data.relatedDocuments.length > 0) {
    return normalizeRelatedDocuments(data.relatedDocuments);
  }
  const numbers: string[] = Array.isArray(data?.transportRouteNumbers) ? data.transportRouteNumbers
    : Array.isArray(data?.wzNumbers) ? data.wzNumbers
    : Array.isArray(data?.routeNumbers) ? data.routeNumbers : [];
  const type = Array.isArray(data?.wzNumbers) ? 'WZ' : 'TRANSPORT';
  return normalizeRelatedDocuments(numbers.map((number) => ({ type, number })));
}

export function getOrderErrorMessage(
  error: any,
  t: any,
  fallbackKey: string,
  fallbackParams?: Record<string, unknown>,
): string {
  const data = error?.response?.data;
  const code = data?.code;
  if (code === 'INVOICE_APPROVAL_REQUIRED') {
    return t(invoiceApprovalRequiredKey());
  }
  if (typeof code === 'string' && ORDER_ERROR_CODE_KEYS[code]) {
    const relatedDocuments = relatedDocumentsFromPayload(data);
    if (relatedDocuments.length > 0) {
      return getBlockedActionMessage({ code, relatedDocuments }, t, 'orders', ORDER_ERROR_CODE_KEYS);
    }
    return t(ORDER_ERROR_CODE_KEYS[code]);
  }
  return getApiErrorMessage(error, t, fallbackKey, undefined, fallbackParams);
}
